import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  FieldMapping,
  LogicalField,
  LOGICAL_FIELD_LABELS,
  LOGICAL_FIELDS_BY_SOURCE_TYPE,
  SourceType,
} from '../../types/dataSource';

interface FieldMappingsEditorProps {
  sourceType: SourceType;
  mappings: FieldMapping[];
  onChange: (mappings: FieldMapping[]) => void;
  disabled?: boolean;
  /** Column names known for this source (from an ArcGIS test connection);
   *  offered as suggestions, free text is always allowed. */
  columnOptions?: string[];
}

export default function FieldMappingsEditor({
  sourceType,
  mappings,
  onChange,
  disabled = false,
  columnOptions = [],
}: FieldMappingsEditorProps) {
  const availableFields = LOGICAL_FIELDS_BY_SOURCE_TYPE[sourceType];

  const handleAdd = () => {
    const firstUnused = availableFields.find(
      (f) => !mappings.some((m) => m.logical_field === f),
    );
    if (!firstUnused) return;
    onChange([...mappings, { logical_field: firstUnused, source_column: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(mappings.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, patch: Partial<FieldMapping>) => {
    onChange(mappings.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const isDateField = (field: LogicalField) => {
    return field === 'start_date' || field === 'end_date' || field === 'veg_date';
  };

  if (availableFields.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Field Mappings
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Map the source dataset's column names to the standard fields used by this application.
      </Typography>

      {mappings.map((mapping, index) => {
        const usedFields = mappings
          .map((m, i) => (i !== index ? m.logical_field : null))
          .filter(Boolean) as LogicalField[];
        const showDateOptions = isDateField(mapping.logical_field);

        return (
          <Box key={index} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: showDateOptions ? 1 : 0 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Logical Field</InputLabel>
                <Select
                  value={mapping.logical_field}
                  label="Logical Field"
                  disabled={disabled}
                  onChange={(e) =>
                    handleChange(index, { logical_field: e.target.value as LogicalField })
                  }
                >
                  {availableFields.map((f) => (
                    <MenuItem key={f} value={f} disabled={usedFields.includes(f)}>
                      {LOGICAL_FIELD_LABELS[f]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                freeSolo
                size="small"
                options={columnOptions}
                value={mapping.source_column}
                disabled={disabled}
                onInputChange={(_e, value) => handleChange(index, { source_column: value })}
                sx={{ flex: 1 }}
                renderInput={(params) => <TextField {...params} label="Source Column" />}
              />

              <Tooltip title="Remove mapping">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {showDateOptions && (
              <Box sx={{ pl: 0.5 }}>
                <TextField
                  size="small"
                  label="Date Format (optional)"
                  value={mapping.date_format || ''}
                  disabled={disabled}
                  onChange={(e) => handleChange(index, { date_format: e.target.value || undefined })}
                  placeholder="e.g. YYYYMM or leave blank for auto"
                  fullWidth
                  helperText="Leave blank for auto-detect, or specify format like YYYYMM for 201710, YYYY-MM-DD, DD/MM/YYYY, etc."
                />
              </Box>
            )}
          </Box>
        );
      })}

      {!disabled && mappings.length < availableFields.length && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{ mt: 0.5 }}
        >
          Add mapping
        </Button>
      )}
    </Box>
  );
}
